import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { PickerWorker, StaffingCellData } from 'admin/src/screens/admin/Pollworker/StaffingChart';
import type { AvailabilityDateDto, LocationStaffingResponse, WorkerScheduleItem } from 'admin/src/types/Pollworker/LocationStaffing';

export type FilterOption = 'all' | 'incomplete-roles' | 'incomplete-days' | 'rogue-only' | 'unreleased-only' | 'rejected' | 'pending-confirmation' | 'unavailable-workers';
export type ConfirmationSidebarFilter = 'all' | 'rejected' | 'pending';
export type ChartMode = 'survey' | 'planning' | 'scheduling';
export type ConfirmationGroupBy = 'worker' | 'location' | 'date';
export type SidebarTab = 'confirmations' | 'detail';
export type SurveyWorkerFilter = 'no-responses' | 'no-available';
export type SurveyDayFilter = 'no-responses' | 'no-available';
export type SurveySortBy = 'name';
export type SurveySortDir = 'asc' | 'desc';

export interface CellKey {
  locationId: string;
  roleId: string;
  date: string;
}

export interface HighlightedCell extends CellKey {
  animate: boolean;
}

export interface AddedRole {
  locationId: string;
  roleId: string;
  roleName: string;
  instanceIndex: number;
}

export interface RogueConversionDialogDate {
  date: string;
  currentAssignments: number;
  suggestedCount: number;
}

export interface RogueConversionDialog {
  isOpen: boolean;
  locationId: string;
  roleId: string;
  roleName: string;
  dates: RogueConversionDialogDate[];
  pendingCellOverrides: Record<string, StaffingCellData>;
}

export enum MimicPhase {
  IDLE = 'idle',
  AWAITING_SOURCE = 'awaitingSource',
  SELECTING_TARGETS = 'selectingTargets',
  CONFIRMING = 'confirming',
}
export type MimicSourceType = 'location' | 'role' | 'date' | null;

export interface MimicSource {
  locationId?: string;
  locationName?: string;
  roleId?: string;
  roleName?: string;
  date?: string;
}

export interface MimicTarget {
  locationId?: string;
  roleId?: string;
  date?: string;
}

export type MimicReplaceMode = 'all' | 'matchExisting';

export interface MimicState {
  phase: MimicPhase;
  sourceType: MimicSourceType;
  source: MimicSource | null;
  selectedTargets: MimicTarget[];
  replaceMode: MimicReplaceMode;
}

export interface ActivePickerCell {
  locationId: string;
  roleId: string;
  date: string;
}

export interface SelectionAnchor {
  rowIndex: number;
  colIndex: number;
}

export type StaffingState = {
  staffingResponses: Record<string, LocationStaffingResponse>;
  schedules: WorkerScheduleItem[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  cellOverrides: Record<string, StaffingCellData>;
  addedRoles: AddedRole[];
  removedRoles: string[];
  pendingSaveLocations: string[];
  pendingScheduleCells: string[];
  electionId: string | null;
  dateStart: string | null;
  dateEnd: string | null;
  electionDate: string | null;
  highlightEmpty: boolean;
  filter: FilterOption;
  filters: FilterOption[];
  chartMode: ChartMode;
  draggedWorker: PickerWorker | null;
  dragOverCell: string | null;
  expandedCells: string[];
  superCopyActive: boolean;
  superCopySourceCell: CellKey | null;
  superCopySourceCellData: StaffingCellData | null;
  superCopySelectedCells: string[];
  multiSelectActive: boolean;
  multiSelectSelectedCells: string[];
  rogueConfirmation: {
    isOpen: boolean;
    locationId: string | null;
    roleId: string | null;
    date: string | null;
    workerCount: number;
    roleName: string | null;
    pendingWorkerIds: string[];
  } | null;
  availability: {
    surveyId: string | null;
    dateAvailability: AvailabilityDateDto[];
    isLoading: boolean;
    error: string | null;
  };
  surveyDraggedWorkers: PickerWorker[];
  isSurveyDragOver: boolean;
  surveySelectedWorkerIds: string[];
  workerPickerDocked: boolean;
  activePickerCell: ActivePickerCell | null;
  confirmationSidebarOpen: boolean;
  confirmationSidebarFilter: ConfirmationSidebarFilter;
  highlightedCell: HighlightedCell | null;
  surveyWorkerFilters: SurveyWorkerFilter[];
  surveyDayFilters: SurveyDayFilter[];
  surveySortBy: SurveySortBy | null;
  surveySortDir: SurveySortDir;
  rogueConversionDialog: RogueConversionDialog | null;
  mimicMode: MimicState;
  lastSaveResult: {
    success: boolean;
    message: string;
    timestamp: number;
  } | null;
  confirmationGroupBy: ConfirmationGroupBy;
  selectedScheduleIds: string[];
  confirmationSaving: boolean;
  confirmationSaveError: string | null;
  updatingScheduleIds: string[];
  scheduleTimeRollbacks: Record<string, { startTime: string; endTime: string }>;
  sidebarActiveTab: SidebarTab;
  selectionAnchor: SelectionAnchor | null;
  activeStatsTooltip: string | null;
};

const WORKER_PICKER_DOCKED_KEY = 'staffing:workerPickerDocked';

function getInitialWorkerPickerDocked(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return localStorage.getItem(WORKER_PICKER_DOCKED_KEY) !== 'false';
  } catch {
    return true;
  }
}

const initialState: StaffingState = {
  staffingResponses: {},
  schedules: [],
  isLoading: false,
  isSaving: false,
  error: null,
  cellOverrides: {},
  addedRoles: [],
  removedRoles: [],
  pendingSaveLocations: [],
  pendingScheduleCells: [],
  electionId: null,
  dateStart: null,
  dateEnd: null,
  electionDate: null,
  highlightEmpty: false,
  filter: 'all',
  filters: [],
  chartMode: 'planning',
  draggedWorker: null,
  dragOverCell: null,
  expandedCells: [],
  superCopyActive: false,
  superCopySourceCell: null,
  superCopySourceCellData: null,
  superCopySelectedCells: [],
  multiSelectActive: false,
  multiSelectSelectedCells: [],
  rogueConfirmation: null,
  availability: {
    surveyId: null,
    dateAvailability: [],
    isLoading: false,
    error: null,
  },
  surveyDraggedWorkers: [],
  isSurveyDragOver: false,
  surveySelectedWorkerIds: [],
  workerPickerDocked: getInitialWorkerPickerDocked(),
  activePickerCell: null,
  confirmationSidebarOpen: false,
  confirmationSidebarFilter: 'all',
  highlightedCell: null,
  surveyWorkerFilters: [],
  surveyDayFilters: [],
  surveySortBy: null,
  surveySortDir: 'asc',
  rogueConversionDialog: null,
  mimicMode: {
    phase: MimicPhase.IDLE,
    sourceType: null,
    source: null,
    selectedTargets: [],
    replaceMode: 'all',
  },
  lastSaveResult: null,
  confirmationGroupBy: 'worker',
  selectedScheduleIds: [],
  confirmationSaving: false,
  confirmationSaveError: null,
  updatingScheduleIds: [],
  scheduleTimeRollbacks: {},
  sidebarActiveTab: 'confirmations',
  selectionAnchor: null,
  activeStatsTooltip: null,
};

export interface FetchStaffingStartPayload {
  electionId: string;
  dateStart: string;
  dateEnd: string;
  electionDate: string;
}

export interface FetchStaffingSuccessPayload {
  staffingResponses: Record<string, LocationStaffingResponse>;
  schedules: WorkerScheduleItem[];
}

export interface UpdateCellPayload {
  locationId: string;
  roleId: string;
  instanceIndex: number;
  date: string;
  data: StaffingCellData;
}

export interface BatchUpdateCellsPayload {
  cells: UpdateCellPayload[];
}

export interface AddRolePayload {
  locationId: string;
  roleId: string;
  roleName: string;
}

export interface RemoveRolePayload {
  locationId: string;
  roleId: string;
}

export interface AssignWorkersPayload {
  locationId: string;
  roleId: string;
  date: string;
  workerIds: string[];
  startTime?: string;
  endTime?: string;
}

export interface UnassignWorkersPayload {
  locationId: string;
  roleId: string;
  date: string;
  scheduleIds: string[];
}

export interface UpdateScheduleTimePayload {
  scheduleId: string;
  startTime: string;
  endTime: string;
}

export interface SavePositionsSuccessPayload {
  locationIds: string[];
  message?: string;
}

export interface StartAddRolesPayload {
  locationIds: string[];
  roles: Array<{ roleId: string; roleName: string }>;
}

export interface ShowRogueConversionDialogPayload {
  locationId: string;
  roleId: string;
  roleName: string;
  dates: RogueConversionDialogDate[];
  pendingCellOverrides: Record<string, StaffingCellData>;
}

export interface UpdateRogueConversionCountPayload {
  date: string;
  count: number;
}

export interface SetMimicSourcePayload {
  sourceType: 'location' | 'role' | 'date';
  source: MimicSource;
}

export interface ToggleMimicTargetPayload {
  target: MimicTarget;
}

export interface RowInteractionPayload {
  locationId: string;
  roleId?: string;
  locationName: string;
  roleName?: string;
  isRogue?: boolean;
  isAdded?: boolean;
  allCellsForRow: { locationId: string; roleId: string; date: string }[];
}

export interface BatchUpdateConfirmationPayload {
  scheduleIds: string[];
  status: number;
}

function makeCellKey(locationId: string, roleId: string, date: string): string {
  return `${locationId}|${roleId}|${date}`;
}

function makeScheduleCellKey(locationId: string, roleId: string, date: string): string {
  return `${locationId}-${roleId}-${date}`;
}

const staffingSlice = createSlice({
  name: 'staffing',
  initialState,
  reducers: {
    fetchStaffingStart(state, action: PayloadAction<FetchStaffingStartPayload>) {
      const electionChanged = state.electionId !== null && state.electionId !== action.payload.electionId;

      state.isLoading = true;
      state.error = null;
      state.electionId = action.payload.electionId;
      state.dateStart = action.payload.dateStart;
      state.dateEnd = action.payload.dateEnd;
      state.electionDate = action.payload.electionDate;

      if (electionChanged) {
        state.filters = [];
      }
    },
    fetchStaffingSuccess(state, action: PayloadAction<FetchStaffingSuccessPayload>) {
      state.isLoading = false;
      state.staffingResponses = action.payload.staffingResponses;
      state.schedules = action.payload.schedules;
      state.cellOverrides = {};
      state.addedRoles = [];
      state.removedRoles = [];
      state.rogueConversionDialog = null;
    },
    fetchStaffingError(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },
    updateCell(state, action: PayloadAction<UpdateCellPayload>) {
      const { locationId, roleId, date, data } = action.payload;
      const key = makeCellKey(locationId, roleId, date);
      state.cellOverrides[key] = data;
      if (!state.pendingSaveLocations.includes(locationId)) {
        state.pendingSaveLocations.push(locationId);
      }
    },
    batchUpdateCells(state, action: PayloadAction<BatchUpdateCellsPayload>) {
      const { cells } = action.payload;
      for (const cell of cells) {
        const { locationId, roleId, date, data } = cell;
        const key = makeCellKey(locationId, roleId, date);
        state.cellOverrides[key] = data;
        if (!state.pendingSaveLocations.includes(locationId)) {
          state.pendingSaveLocations.push(locationId);
        }
      }
    },
    addRole(state, action: PayloadAction<AddRolePayload>) {
      const { locationId, roleId, roleName } = action.payload;
      const existingCount = state.addedRoles.filter((ar) => ar.locationId === locationId && ar.roleId === roleId).length;
      state.addedRoles.push({ locationId, roleId, roleName, instanceIndex: existingCount });
    },
    removeRole(state, action: PayloadAction<RemoveRolePayload>) {
      const { locationId, roleId } = action.payload;
      const key = `${locationId}|${roleId}`;

      if (!state.removedRoles.includes(key)) state.removedRoles.push(key);
      if (!state.pendingSaveLocations.includes(locationId)) state.pendingSaveLocations.push(locationId);
    },
    assignWorkersStart(state, action: PayloadAction<AssignWorkersPayload>) {
      const { locationId, roleId, date } = action.payload;
      const cellKey = makeScheduleCellKey(locationId, roleId, date);
      if (!state.pendingScheduleCells.includes(cellKey)) {
        state.pendingScheduleCells.push(cellKey);
      }
    },
    assignWorkersSuccess(state, action: PayloadAction<{ cellKey: string }>) {
      const { cellKey } = action.payload;
      state.pendingScheduleCells = state.pendingScheduleCells.filter((k) => k !== cellKey);
    },
    assignWorkersError(state, action: PayloadAction<{ cellKey: string; error: string }>) {
      const { cellKey } = action.payload;
      state.pendingScheduleCells = state.pendingScheduleCells.filter((k) => k !== cellKey);
    },
    unassignWorkersStart(state, action: PayloadAction<UnassignWorkersPayload>) {
      const { locationId, roleId, date } = action.payload;
      const cellKey = makeScheduleCellKey(locationId, roleId, date);
      if (!state.pendingScheduleCells.includes(cellKey)) {
        state.pendingScheduleCells.push(cellKey);
      }
    },
    unassignWorkersSuccess(state, action: PayloadAction<{ cellKey: string; deletedIds: string[] }>) {
      const { cellKey, deletedIds } = action.payload;
      state.pendingScheduleCells = state.pendingScheduleCells.filter((k) => k !== cellKey);
      state.schedules = state.schedules.filter((s) => !deletedIds.includes(s.id));
    },
    unassignWorkersError(state, action: PayloadAction<{ cellKey: string; error: string }>) {
      const { cellKey } = action.payload;
      state.pendingScheduleCells = state.pendingScheduleCells.filter((k) => k !== cellKey);
    },
    updateScheduleTimeStart(state, action: PayloadAction<UpdateScheduleTimePayload>) {
      const { scheduleId, startTime, endTime } = action.payload;
      const schedule = state.schedules.find((s) => s.id === scheduleId);
      if (schedule) {
        if (!state.scheduleTimeRollbacks[scheduleId]) {
          state.scheduleTimeRollbacks[scheduleId] = {
            startTime: schedule.startTime,
            endTime: schedule.endTime,
          };
        }
        schedule.startTime = startTime;
        schedule.endTime = endTime;
      }
      if (!state.updatingScheduleIds.includes(scheduleId)) {
        state.updatingScheduleIds.push(scheduleId);
      }
    },
    updateScheduleTimeSuccess(state, action: PayloadAction<{ scheduleId: string }>) {
      const { scheduleId } = action.payload;
      state.updatingScheduleIds = state.updatingScheduleIds.filter((id) => id !== scheduleId);
      delete state.scheduleTimeRollbacks[scheduleId];
    },
    updateScheduleTimeError(state, action: PayloadAction<{ scheduleId: string; error: string }>) {
      const { scheduleId } = action.payload;
      const rollback = state.scheduleTimeRollbacks[scheduleId];
      if (rollback) {
        const schedule = state.schedules.find((s) => s.id === scheduleId);
        if (schedule) {
          schedule.startTime = rollback.startTime;
          schedule.endTime = rollback.endTime;
        }
        delete state.scheduleTimeRollbacks[scheduleId];
      }
      state.updatingScheduleIds = state.updatingScheduleIds.filter((id) => id !== scheduleId);
    },
    savePositionsStart(state) {
      state.isSaving = true;
    },
    savePositionsSuccess(state, action: PayloadAction<SavePositionsSuccessPayload>) {
      state.isSaving = false;
      state.pendingSaveLocations = state.pendingSaveLocations.filter((loc) => !action.payload.locationIds.includes(loc));
      state.addedRoles = state.addedRoles.filter((ar) => !action.payload.locationIds.includes(ar.locationId));
      if (action.payload.message) {
        state.lastSaveResult = {
          success: true,
          message: action.payload.message,
          timestamp: Date.now(),
        };
      }
    },
    savePositionsError(state, action: PayloadAction<string>) {
      state.isSaving = false;
      state.error = action.payload;
      state.lastSaveResult = {
        success: false,
        message: `Save failed: ${action.payload}`,
        timestamp: Date.now(),
      };
    },
    refetch(_state) {}, // noop, saga
    silentRefetchSuccess(state, action: PayloadAction<FetchStaffingSuccessPayload>) {
      state.staffingResponses = action.payload.staffingResponses;
      state.schedules = action.payload.schedules;
      state.cellOverrides = {};
      state.addedRoles = [];
      state.removedRoles = [];
      state.rogueConversionDialog = null;
    },
    clearPendingSaveLocations(state, action: PayloadAction<string[]>) {
      state.pendingSaveLocations = state.pendingSaveLocations.filter((loc) => !action.payload.includes(loc));
    },
    setHighlightEmpty(state, action: PayloadAction<boolean>) {
      state.highlightEmpty = action.payload;
    },
    setFilter(state, action: PayloadAction<FilterOption>) {
      state.filter = action.payload;
    },
    setFilters(state, action: PayloadAction<FilterOption[]>) {
      state.filters = action.payload;
    },
    toggleFilter(state, action: PayloadAction<FilterOption>) {
      const filter = action.payload;
      const index = state.filters.indexOf(filter);
      if (index >= 0) {
        state.filters.splice(index, 1);
      } else {
        state.filters.push(filter);
      }
    },
    clearFilters(state) {
      state.filters = [];
    },
    setChartMode(state, action: PayloadAction<ChartMode>) {
      state.chartMode = action.payload;
      state.expandedCells = [];
      state.draggedWorker = null;
      state.dragOverCell = null;
    },
    toggleCellExpanded(state, action: PayloadAction<string>) {
      const cellKey = action.payload;
      const index = state.expandedCells.indexOf(cellKey);
      if (index >= 0) {
        state.expandedCells.splice(index, 1);
      } else {
        state.expandedCells.push(cellKey);
      }
    },
    startSuperCopy(state, action: PayloadAction<{ cell: CellKey; cellData: StaffingCellData }>) {
      state.superCopyActive = true;
      state.superCopySourceCell = action.payload.cell;
      state.superCopySourceCellData = action.payload.cellData;
      state.superCopySelectedCells = [];
      state.multiSelectActive = false;
      state.multiSelectSelectedCells = [];
      state.mimicMode = {
        phase: MimicPhase.IDLE,
        sourceType: null,
        source: null,
        selectedTargets: [],
        replaceMode: 'all',
      };
    },
    toggleSuperCopyCell(state, action: PayloadAction<CellKey>) {
      if (!state.superCopyActive) return;
      const key = `${action.payload.locationId}|${action.payload.roleId}|${action.payload.date}`;
      const sourceKey = state.superCopySourceCell ? `${state.superCopySourceCell.locationId}|${state.superCopySourceCell.roleId}|${state.superCopySourceCell.date}` : null;
      if (key === sourceKey) return;
      const index = state.superCopySelectedCells.indexOf(key);
      if (index >= 0) {
        state.superCopySelectedCells.splice(index, 1);
      } else {
        state.superCopySelectedCells.push(key);
      }
    },
    cancelSuperCopy(state) {
      state.superCopyActive = false;
      state.superCopySourceCell = null;
      state.superCopySourceCellData = null;
      state.superCopySelectedCells = [];
      state.selectionAnchor = null;
    },
    applySuperCopy(state) {
      state.superCopyActive = false;
      state.superCopySourceCell = null;
      state.superCopySourceCellData = null;
      state.superCopySelectedCells = [];
      state.selectionAnchor = null;
    },
    toggleMultiSelectCell(state, action: PayloadAction<{ cell: CellKey; forceAdd?: boolean }>) {
      const key = `${action.payload.cell.locationId}|${action.payload.cell.roleId}|${action.payload.cell.date}`;
      if (action.payload.forceAdd) {
        if (!state.multiSelectSelectedCells.includes(key)) {
          state.multiSelectSelectedCells.push(key);
        }
      } else {
        const index = state.multiSelectSelectedCells.indexOf(key);
        if (index >= 0) {
          state.multiSelectSelectedCells.splice(index, 1);
        } else {
          state.multiSelectSelectedCells.push(key);
        }
      }
      state.multiSelectActive = state.multiSelectSelectedCells.length > 0;
      if (state.multiSelectActive) {
        state.superCopyActive = false;
        state.superCopySourceCell = null;
        state.superCopySourceCellData = null;
        state.superCopySelectedCells = [];
        state.mimicMode = {
          phase: MimicPhase.IDLE,
          replaceMode: 'all',
          sourceType: null,
          source: null,
          selectedTargets: [],
        };
      }
    },
    clearMultiSelection(state) {
      state.multiSelectActive = false;
      state.multiSelectSelectedCells = [];
      state.selectionAnchor = null;
    },
    setSelectionAnchor(state, action: PayloadAction<{ rowIndex: number; colIndex: number }>) {
      state.selectionAnchor = action.payload;
    },
    shiftSelectCells(
      state,
      action: PayloadAction<{
        targetCell: CellKey;
        targetRowIndex: number;
        targetColIndex: number;
        allCellsInRange: CellKey[];
      }>,
    ) {
      const { allCellsInRange, targetRowIndex, targetColIndex } = action.payload;

      if (!state.selectionAnchor) {
        state.selectionAnchor = { rowIndex: targetRowIndex, colIndex: targetColIndex };
      }

      if (state.superCopyActive) {
        const sourceKey = state.superCopySourceCell ? `${state.superCopySourceCell.locationId}|${state.superCopySourceCell.roleId}|${state.superCopySourceCell.date}` : null;
        for (const cell of allCellsInRange) {
          const key = `${cell.locationId}|${cell.roleId}|${cell.date}`;
          if (key !== sourceKey && !state.superCopySelectedCells.includes(key)) {
            state.superCopySelectedCells.push(key);
          }
        }
      } else {
        for (const cell of allCellsInRange) {
          const key = `${cell.locationId}|${cell.roleId}|${cell.date}`;
          if (!state.multiSelectSelectedCells.includes(key)) {
            state.multiSelectSelectedCells.push(key);
          }
        }
        state.multiSelectActive = state.multiSelectSelectedCells.length > 0;
        if (state.multiSelectActive) {
          state.superCopyActive = false;
          state.superCopySourceCell = null;
          state.superCopySourceCellData = null;
          state.superCopySelectedCells = [];
          state.mimicMode = {
            phase: MimicPhase.IDLE,
            replaceMode: 'all',
            sourceType: null,
            source: null,
            selectedTargets: [],
          };
        }
      }
    },
    showRogueConfirmation(
      state,
      action: PayloadAction<{
        locationId: string;
        roleId: string;
        date: string;
        workerCount: number;
        roleName: string;
        pendingWorkerIds: string[];
      }>,
    ) {
      state.rogueConfirmation = {
        isOpen: true,
        ...action.payload,
      };
    },
    confirmRoguePositionCreate(state) {
      if (!state.rogueConfirmation) return;

      state.rogueConfirmation.isOpen = false;
    },
    cancelRogueConfirmation(state) {
      state.rogueConfirmation = null;
    },
    clearRogueConfirmation(state) {
      state.rogueConfirmation = null;
    },
    startAddRoles(state, action: PayloadAction<StartAddRolesPayload>) {
      const { locationIds, roles } = action.payload;
      for (const locationId of locationIds) {
        for (const role of roles) {
          const alreadyAdded = state.addedRoles.some((ar) => ar.locationId === locationId && ar.roleId === role.roleId);
          if (alreadyAdded) continue;

          const existingCount = state.addedRoles.filter((ar) => ar.locationId === locationId && ar.roleId === role.roleId).length;
          state.addedRoles.push({
            locationId,
            roleId: role.roleId,
            roleName: role.roleName,
            instanceIndex: existingCount,
          });

          if (!state.pendingSaveLocations.includes(locationId)) {
            state.pendingSaveLocations.push(locationId);
          }
        }
      }
    },
    fetchAvailabilityStart(state) {
      state.availability.isLoading = true;
      state.availability.error = null;
    },
    fetchAvailabilitySuccess(state, action: PayloadAction<{ surveyId: string | null; dateAvailability: AvailabilityDateDto[] }>) {
      state.availability.isLoading = false;
      state.availability.surveyId = action.payload.surveyId;
      state.availability.dateAvailability = action.payload.dateAvailability;
    },
    fetchAvailabilityError(state, action: PayloadAction<string>) {
      state.availability.isLoading = false;
      state.availability.error = action.payload;
    },
    setSurveyDraggedWorkers(state, action: PayloadAction<PickerWorker[]>) {
      state.surveyDraggedWorkers = action.payload;
    },
    setSurveyDragOver(state, action: PayloadAction<boolean>) {
      state.isSurveyDragOver = action.payload;
    },
    clearSurveyDrag(state) {
      state.surveyDraggedWorkers = [];
      state.isSurveyDragOver = false;
    },
    setSurveySelectedWorkerIds(state, action: PayloadAction<string[]>) {
      state.surveySelectedWorkerIds = action.payload;
    },
    toggleSurveySelectedWorkerId(state, action: PayloadAction<string>) {
      const id = action.payload;
      const index = state.surveySelectedWorkerIds.indexOf(id);
      if (index >= 0) {
        state.surveySelectedWorkerIds.splice(index, 1);
      } else {
        state.surveySelectedWorkerIds.push(id);
      }
    },
    clearSurveySelectedWorkerIds(state) {
      state.surveySelectedWorkerIds = [];
    },
    setWorkerPickerDocked(state, action: PayloadAction<boolean>) {
      state.workerPickerDocked = action.payload;
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(WORKER_PICKER_DOCKED_KEY, action.payload ? 'true' : 'false');
        } catch {
          // Ignore localStorage errors
        }
      }
    },
    setActivePickerCell(state, action: PayloadAction<ActivePickerCell | null>) {
      state.activePickerCell = action.payload;
      if (action.payload) {
        state.sidebarActiveTab = 'detail';
      }
    },
    setSidebarActiveTab(state, action: PayloadAction<SidebarTab>) {
      state.sidebarActiveTab = action.payload;
    },
    closeConfirmationSidebar(state) {
      state.confirmationSidebarOpen = false;
      state.highlightedCell = null;
    },
    setConfirmationSidebarFilter(state, action: PayloadAction<ConfirmationSidebarFilter>) {
      state.confirmationSidebarFilter = action.payload;
    },
    setHighlightedCell(state, action: PayloadAction<HighlightedCell>) {
      state.highlightedCell = action.payload;
    },
    clearHighlightedCell(state) {
      state.highlightedCell = null;
    },
    setSurveyWorkerFilters(state, action: PayloadAction<SurveyWorkerFilter[]>) {
      state.surveyWorkerFilters = action.payload;
    },
    setSurveyDayFilters(state, action: PayloadAction<SurveyDayFilter[]>) {
      state.surveyDayFilters = action.payload;
    },
    setSurveySortBy(state, action: PayloadAction<SurveySortBy | null>) {
      state.surveySortBy = action.payload;
    },
    setSurveySortDir(state, action: PayloadAction<SurveySortDir>) {
      state.surveySortDir = action.payload;
    },
    clearSurveyFilters(state) {
      state.surveyWorkerFilters = [];
      state.surveyDayFilters = [];
      state.surveySortBy = null;
      state.surveySortDir = 'asc';
    },
    showRogueConversionDialog(state, action: PayloadAction<ShowRogueConversionDialogPayload>) {
      state.rogueConversionDialog = {
        isOpen: true,
        locationId: action.payload.locationId,
        roleId: action.payload.roleId,
        roleName: action.payload.roleName,
        dates: action.payload.dates,
        pendingCellOverrides: action.payload.pendingCellOverrides,
      };
    },
    updateRogueConversionCount(state, action: PayloadAction<UpdateRogueConversionCountPayload>) {
      if (!state.rogueConversionDialog) return;
      const dateEntry = state.rogueConversionDialog.dates.find((d) => d.date === action.payload.date);
      if (dateEntry) {
        dateEntry.suggestedCount = action.payload.count;
      }
    },
    confirmRogueConversion(state) {
      if (!state.rogueConversionDialog) return;
      state.rogueConversionDialog.isOpen = false;
    },
    cancelRogueConversion(state) {
      if (!state.rogueConversionDialog) return;
      for (const key of Object.keys(state.rogueConversionDialog.pendingCellOverrides)) {
        delete state.cellOverrides[key];
      }
      const locationId = state.rogueConversionDialog.locationId;
      state.pendingSaveLocations = state.pendingSaveLocations.filter((loc) => loc !== locationId);
      state.rogueConversionDialog = null;
    },
    clearRogueConversionDialog(state) {
      state.rogueConversionDialog = null;
    },
    startMimicMode(state) {
      state.mimicMode = {
        phase: MimicPhase.AWAITING_SOURCE,
        sourceType: null,
        source: null,
        selectedTargets: [],
        replaceMode: 'all',
      };
      state.superCopyActive = false;
      state.superCopySourceCell = null;
      state.superCopySourceCellData = null;
      state.superCopySelectedCells = [];
      state.multiSelectActive = false;
      state.multiSelectSelectedCells = [];
    },
    setMimicSource(state, action: PayloadAction<SetMimicSourcePayload>) {
      state.mimicMode.phase = MimicPhase.SELECTING_TARGETS;
      state.mimicMode.sourceType = action.payload.sourceType;
      state.mimicMode.source = action.payload.source;
      state.mimicMode.selectedTargets = [];
    },
    toggleMimicTarget(state, action: PayloadAction<ToggleMimicTargetPayload>) {
      if (state.mimicMode.phase !== MimicPhase.SELECTING_TARGETS) return;
      const { target } = action.payload;
      const { source, sourceType } = state.mimicMode;

      if (sourceType === 'location' && target.locationId === source?.locationId) return;
      if (sourceType === 'role' && target.locationId === source?.locationId && target.roleId === source?.roleId) return;
      if (sourceType === 'date' && target.date === source?.date) return;

      const existingIndex = state.mimicMode.selectedTargets.findIndex((t) => {
        if (state.mimicMode.sourceType === 'location') {
          return t.locationId === target.locationId;
        }
        if (state.mimicMode.sourceType === 'role') {
          return t.locationId === target.locationId && t.roleId === target.roleId;
        }
        if (state.mimicMode.sourceType === 'date') {
          return t.date === target.date;
        }
        return false;
      });
      if (existingIndex >= 0) {
        state.mimicMode.selectedTargets.splice(existingIndex, 1);
      } else {
        state.mimicMode.selectedTargets.push(target);
      }
    },
    confirmMimic(_state) {},
    mimicApplySuccess(state) {
      state.mimicMode = {
        phase: MimicPhase.IDLE,
        sourceType: null,
        source: null,
        selectedTargets: [],
        replaceMode: 'all',
      };
    },
    handleRowInteraction(state, action: PayloadAction<RowInteractionPayload>) {
      const { locationId, roleId, locationName, roleName, isRogue, isAdded, allCellsForRow } = action.payload;

      if (state.mimicMode.phase === MimicPhase.AWAITING_SOURCE) {
        if (isRogue || isAdded) return;
        if (roleId) {
          state.mimicMode.phase = MimicPhase.SELECTING_TARGETS;
          state.mimicMode.sourceType = 'role';
          state.mimicMode.source = { locationId, locationName, roleId, roleName };
          state.mimicMode.selectedTargets = [];
        } else {
          state.mimicMode.phase = MimicPhase.SELECTING_TARGETS;
          state.mimicMode.sourceType = 'location';
          state.mimicMode.source = { locationId, locationName };
          state.mimicMode.selectedTargets = [];
        }
        return;
      }

      if (state.mimicMode.phase === MimicPhase.SELECTING_TARGETS) {
        if (state.mimicMode.sourceType === 'location' && !roleId) {
          const existingIndex = state.mimicMode.selectedTargets.findIndex((t) => t.locationId === locationId);
          if (existingIndex !== -1) {
            state.mimicMode.selectedTargets.splice(existingIndex, 1);
          } else {
            state.mimicMode.selectedTargets.push({ locationId });
          }
          return;
        }
        if (state.mimicMode.sourceType === 'role' && roleId) {
          if (isRogue || isAdded) return;
          const existingIndex = state.mimicMode.selectedTargets.findIndex((t) => t.locationId === locationId && t.roleId === roleId);
          if (existingIndex !== -1) {
            state.mimicMode.selectedTargets.splice(existingIndex, 1);
          } else {
            state.mimicMode.selectedTargets.push({ locationId, roleId });
          }
          return;
        }
      }
    },
    setMimicReplaceMode(state, action: PayloadAction<MimicReplaceMode>) {
      state.mimicMode.replaceMode = action.payload;
    },
    selectAllMimicTargets(state, action: PayloadAction<{ targets: MimicTarget[] }>) {
      if (state.mimicMode.phase !== MimicPhase.SELECTING_TARGETS) return;
      const { source, sourceType } = state.mimicMode;

      state.mimicMode.selectedTargets = action.payload.targets.filter((target) => {
        if (sourceType === 'location' && target.locationId === source?.locationId) return false;
        if (sourceType === 'role' && target.locationId === source?.locationId && target.roleId === source?.roleId) return false;
        if (sourceType === 'date' && target.date === source?.date) return false;
        return true;
      });
    },
    cancelMimicMode(state) {
      state.mimicMode = {
        phase: MimicPhase.IDLE,
        sourceType: null,
        source: null,
        selectedTargets: [],
        replaceMode: 'all',
      };
    },
    clearLastSaveResult(state) {
      state.lastSaveResult = null;
    },
    setConfirmationGroupBy(state, action: PayloadAction<ConfirmationGroupBy>) {
      state.confirmationGroupBy = action.payload;
      state.selectedScheduleIds = [];
    },
    toggleScheduleSelection(state, action: PayloadAction<string>) {
      const scheduleId = action.payload;
      const index = state.selectedScheduleIds.indexOf(scheduleId);
      if (index >= 0) {
        state.selectedScheduleIds.splice(index, 1);
      } else {
        state.selectedScheduleIds.push(scheduleId);
      }
    },
    selectScheduleGroup(state, action: PayloadAction<string[]>) {
      for (const id of action.payload) {
        if (!state.selectedScheduleIds.includes(id)) {
          state.selectedScheduleIds.push(id);
        }
      }
    },
    deselectScheduleGroup(state, action: PayloadAction<string[]>) {
      state.selectedScheduleIds = state.selectedScheduleIds.filter((id) => !action.payload.includes(id));
    },
    selectAllSchedules(state, action: PayloadAction<string[]>) {
      state.selectedScheduleIds = [...action.payload];
    },
    clearScheduleSelection(state) {
      state.selectedScheduleIds = [];
    },
    batchUpdateConfirmationStart(state, action: PayloadAction<BatchUpdateConfirmationPayload>) {
      state.confirmationSaving = true;
      state.confirmationSaveError = null;
      const { scheduleIds, status } = action.payload;
      state.updatingScheduleIds = scheduleIds;
      const scheduleIdSet = new Set(scheduleIds);
      for (const schedule of state.schedules) {
        if (scheduleIdSet.has(schedule.id)) {
          schedule.userConfirmationStatus = status;
        }
      }
    },
    batchUpdateConfirmationSuccess(state, _action: PayloadAction<{ updatedCount: number }>) {
      state.confirmationSaving = false;
      state.selectedScheduleIds = [];
      state.updatingScheduleIds = [];
    },
    batchUpdateConfirmationError(state, action: PayloadAction<string>) {
      state.confirmationSaving = false;
      state.confirmationSaveError = action.payload;
      state.updatingScheduleIds = [];
    },
    setActiveStatsTooltip(state, action: PayloadAction<string | null>) {
      state.activeStatsTooltip = action.payload;
    },
  },
});

export const staffing = staffingSlice.actions;
export default staffingSlice.reducer;
